/* ============================================================================
 * HELLIX ONDERCONSTRUCTIE CALCULATOR — REKENKERN  (v2 — met obstakels)
 * ----------------------------------------------------------------------------
 * Pure functies. Geen DOM, geen Shopify-calls. Eenvoudig testbaar.
 *
 * Input:
 *   state = {
 *     roofType, roofWidthM, roofHeightM, panel:{length,width},
 *     orientation, rows, cols,
 *     blockedCells?: Set<"r,c">    // optioneel; obstakels + handmatig verwijderd
 *   }
 *
 * Output: { summary, panels, materialList }
 *   materialList = [{ sku, qty, blurb }]
 * ==========================================================================*/
(function (root) {
  "use strict";

  const C = () => root.HELLIX_CALC_CONFIG;

  function add(map, sku, qty) {
    if (!sku || qty <= 0) return;
    map.set(sku, (map.get(sku) || 0) + qty);
  }

  /**
   * Kies de meest efficiente combinatie rail-stukken voor een vereiste lengte.
   * Regel: geen snijverlies, minimaal aantal stukken, kleinste totaal-overschot.
   */
  function chooseRailPieces(requiredMm, available) {
    const sorted = [...available].sort((a, b) => a.lengthMm - b.lengthMm);
    const single = sorted.find(r => r.lengthMm >= requiredMm);
    if (single) return [single.sku];
    let best = null;
    for (const a of sorted) for (const b of sorted) {
      const total = a.lengthMm + b.lengthMm;
      if (total >= requiredMm) {
        if (!best || total < best.total) best = { skus: [a.sku, b.sku], total };
      }
    }
    if (best) return best.skus;
    // greedy fallback (zelden)
    const biggest = sorted[sorted.length - 1];
    const pieces = [];
    let rem = requiredMm;
    while (rem > 0) { pieces.push(biggest.sku); rem -= biggest.lengthMm; }
    return pieces;
  }

  /* ------------------------------------------------------------- main ---- */
  function calculate(state) {
    const cfg = C();
    if (!cfg) throw new Error("HELLIX_CALC_CONFIG ontbreekt");

    const { roofType, panel, orientation, rows, cols, roofWidthM, roofHeightM } = state;
    const blocked = state.blockedCells || new Set();
    const roof = cfg.roofTypes[roofType];
    if (!roof) throw new Error("Onbekend daktype: " + roofType);

    const pOnRoof = orientation === "portrait"
      ? { w: panel.width, h: panel.length }
      : { w: panel.length, h: panel.width };
    const m = cfg.mounting;

    // Centreer panel-grid in het dakvlak. Rails + overhang vallen daarmee
    // altijd binnen de roofrect (mits maxFit gerespecteerd is).
    const widthMm  = (roofWidthM  || 0) * 1000;
    const heightMm = (roofHeightM || 0) * 1000;
    const gridW = cols > 0 ? cols * pOnRoof.w + (cols - 1) * m.panelGapMm : 0;
    const gridH = rows > 0 ? rows * pOnRoof.h + (rows - 1) * m.rowGapMm  : 0;
    const offX = Math.max(0, (widthMm  - gridW) / 2);
    const offY = Math.max(0, (heightMm - gridH) / 2);

    // --- Panelen + per-rij analyse ----------------------------------------
    const panels = [];
    const perRow = []; // perRow[r] = lijst kolomindices waar paneel staat
    for (let r = 0; r < rows; r++) perRow.push([]);
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        if (blocked.has(r + "," + c)) continue;
        perRow[r].push(c);
        panels.push({
          row: r, col: c,
          xMm: offX + c * (pOnRoof.w + m.panelGapMm),
          yMm: offY + r * (pOnRoof.h + m.rowGapMm),
          wMm: pOnRoof.w, hMm: pOnRoof.h,
        });
      }
    }
    const totalPanels = panels.length;

    // --- Per-rij rail/anchor/klem aggregeren ------------------------------
    const railsCfg = cfg.rails;
    const railOptions = railsCfg[roof.railType];
    if (!railOptions || !railOptions.length) {
      throw new Error("Geen rails geconfigureerd voor railtype: " + roof.railType);
    }

    let totalRailLines = 0;
    let totalAnchors = 0;
    let totalEndClamps = 0;
    let totalMidClamps = 0;
    let totalCouplers = 0;
    const railSkuTally = new Map();
    let railPiecesPerLineSample = []; // voor summary (eerste niet-lege rij)
    const geometry = { rails: [], anchors: [] };  // voor legplan-visualisatie
    const RAIL_THICKNESS_MM = 40;

    for (let r = 0; r < rows; r++) {
      const cells = perRow[r];
      if (cells.length === 0) continue;

      // Splits cellen in aaneengesloten runs. Bij gaten breekt de rail.
      const runs = [];
      let cur = [cells[0]];
      for (let i = 1; i < cells.length; i++) {
        if (cells[i] === cells[i - 1] + 1) cur.push(cells[i]);
        else { runs.push(cur); cur = [cells[i]]; }
      }
      runs.push(cur);

      const rowYStartMm = offY + r * (pOnRoof.h + m.rowGapMm);
      const railYTop = rowYStartMm + pOnRoof.h * 0.25 - RAIL_THICKNESS_MM / 2;
      const railYBot = rowYStartMm + pOnRoof.h * 0.75 - RAIL_THICKNESS_MM / 2;

      for (const run of runs) {
        const minCol = run[0];
        const maxCol = run[run.length - 1];
        const spanCols = maxCol - minCol + 1;
        const rowSpanMm = spanCols * pOnRoof.w + (spanCols - 1) * m.panelGapMm;
        const railLineLengthMm = rowSpanMm + 2 * m.railOverhangMm;
        const xStartMm = offX + minCol * (pOnRoof.w + m.panelGapMm) - m.railOverhangMm;

        const piecesPerLine = chooseRailPieces(railLineLengthMm, railOptions);
        if (!railPiecesPerLineSample.length) railPiecesPerLineSample = piecesPerLine;
        piecesPerLine.forEach(sku => add(railSkuTally, sku, m.railsPerRow));

        const anchorsPerLine = Math.ceil(railLineLengthMm / m.hookSpacingMaxMm) + 1;
        const railLinesThisRun = m.railsPerRow;
        const couplersPerLine = Math.max(0, piecesPerLine.length - 1);

        totalRailLines += railLinesThisRun;
        totalAnchors  += anchorsPerLine * railLinesThisRun;
        totalCouplers += couplersPerLine * railLinesThisRun;

        // Per run: 2 eindklemmen per rail-lijn (één aan elk uiteinde van de run)
        totalEndClamps += railLinesThisRun * 2 * m.endClampsPerRailEnd;
        // Middenklemmen: (panelen in run − 1) × rails per run
        totalMidClamps += Math.max(0, run.length - 1) * railLinesThisRun;

        // Geometrie: 2 rails per run + haken
        geometry.rails.push({ xMm: xStartMm, yMm: railYTop, wMm: railLineLengthMm, hMm: RAIL_THICKNESS_MM });
        geometry.rails.push({ xMm: xStartMm, yMm: railYBot, wMm: railLineLengthMm, hMm: RAIL_THICKNESS_MM });
        for (let i = 0; i < anchorsPerLine; i++) {
          const t = anchorsPerLine === 1 ? 0.5 : i / (anchorsPerLine - 1);
          const ax = xStartMm + t * railLineLengthMm;
          geometry.anchors.push({ xMm: ax, yMm: railYTop + RAIL_THICKNESS_MM / 2 });
          geometry.anchors.push({ xMm: ax, yMm: railYBot + RAIL_THICKNESS_MM / 2 });
        }
      }
    }

    if (totalPanels === 0) {
      return {
        summary: { totalPanels: 0, rows, cols, orientation, roofType,
                   railPiecesPerLine: [], totalRailLines: 0, totalAnchors: 0,
                   totalEndClamps: 0, totalMidClamps: 0, totalCouplers: 0,
                   totalEndCaps: 0, totalGrooveNuts: 0 },
        panels: [], materialList: [], geometry: { rails: [], anchors: [] },
      };
    }

    const totalEndCaps    = m.endCapsPerRailLine * totalRailLines;
    const railBolts       = m.boltsPerHook  * totalAnchors;
    const clampNuts       = m.boltsPerClamp * (totalEndClamps + totalMidClamps);
    const couplerBolts    = m.boltsPerCoupler * totalCouplers;
    const totalGrooveNuts = railBolts + clampNuts + couplerBolts;

    /* --- Materiaallijst opbouwen ----------------------------------------- */
    const qty = new Map();

    railSkuTally.forEach((q, sku) => add(qty, sku, q));
    add(qty, railsCfg.couplerSku, totalCouplers);
    add(qty, railsCfg.endCapSku,  totalEndCaps);
    add(qty, cfg.clamps.end.sku,    totalEndClamps);
    add(qty, cfg.clamps.middle.sku, totalMidClamps);

    const anc = roof.anchor;
    add(qty, anc.sku, totalAnchors);
    if (roofType === "pannendak") add(qty, anc.boltSku, totalAnchors);
    if (roofType === "steeldeck") add(qty, anc.screwSku, totalAnchors * (anc.screwsPerAnchor || 2));
    if (roofType === "golfplaat") add(qty, anc.connectorSku, totalAnchors);
    add(qty, anc.nutSku || "HX-BV-GM-8", totalGrooveNuts);

    const materialList = [];
    qty.forEach((q, sku) => materialList.push({
      sku, qty: q,
      blurb: (cfg.componentBlurbs && cfg.componentBlurbs[sku]) || "",
    }));

    const order = (sku) => {
      if (sku.startsWith("HX-RA") || sku.startsWith("HX-SD-RA")) return 1;
      if (sku.startsWith("HX-DH") || sku.startsWith("HX-SD-BG") ||
          sku.startsWith("HX-GC-CL") || sku.startsWith("HX-SD-TV")) return 2;
      if (sku.startsWith("HX-KL")) return 3;
      if (sku.startsWith("HX-GC-HV")) return 4;
      return 5;
    };
    materialList.sort((a, b) => order(a.sku) - order(b.sku) || a.sku.localeCompare(b.sku));

    return {
      summary: {
        totalPanels, rows, cols, orientation, roofType,
        railPiecesPerLine: railPiecesPerLineSample,
        totalRailLines, totalAnchors,
        totalEndClamps, totalMidClamps,
        totalCouplers, totalEndCaps, totalGrooveNuts,
        offsetXMm: offX, offsetYMm: offY,
      },
      panels,
      materialList,
      geometry,
    };
  }

  /**
   * Max raster dat op het dak past.
   */
  function maxFit(roofWidthM, roofHeightM, panel, orientation) {
    const m = C().mounting;
    const pl = panel.length, pw = panel.width;
    const onRoof = orientation === "portrait" ? { w: pw, h: pl } : { w: pl, h: pw };
    // Houd marge voor de rail-overhang aan beide zijden zodat de bevestiging
    // altijd binnen het dakvlak valt.
    const usableW = Math.max(0, roofWidthM  * 1000 - 2 * m.railOverhangMm);
    const usableH = Math.max(0, roofHeightM * 1000 - 2 * m.railOverhangMm);
    const cols = Math.max(0, Math.floor((usableW + m.panelGapMm) / (onRoof.w + m.panelGapMm)));
    const rows = Math.max(0, Math.floor((usableH + m.rowGapMm) / (onRoof.h + m.rowGapMm)));
    return { rows, cols };
  }

  /**
   * Bereken welke cellen geblokkeerd zijn door obstakels (AABB-overlap).
   * obstacles = [{xMm,yMm,wMm,hMm}]
   * Returns Set<"r,c">
   */
  function computeBlocked(rows, cols, panelOnRoof, mounting, obstacles, offset) {
    const blocked = new Set();
    if (!obstacles || !obstacles.length) return blocked;
    const offX = (offset && offset.x) || 0;
    const offY = (offset && offset.y) || 0;
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const px = offX + c * (panelOnRoof.w + mounting.panelGapMm);
        const py = offY + r * (panelOnRoof.h + mounting.rowGapMm);
        const pw = panelOnRoof.w, ph = panelOnRoof.h;
        for (const o of obstacles) {
          if (px < o.xMm + o.wMm && px + pw > o.xMm &&
              py < o.yMm + o.hMm && py + ph > o.yMm) {
            blocked.add(r + "," + c); break;
          }
        }
      }
    }
    return blocked;
  }

  root.HellixCalcEngine = { calculate, chooseRailPieces, maxFit, computeBlocked };
})(window);
